import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { CheckpointPhoto } from "@/components/site/CheckpointPhoto";
import { SectionCta } from "@/components/site/SectionCta";
import { ES_PAGE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";
import { esHead, esWebPageSchema } from "@/lib/es-pages";
import { EsCoverageTable } from "@/components/site/EsCoverageTable";
import { ES_FAQS } from "@/lib/es-insurance-va";
import { FaqList, faqSchema } from "@/components/site/InsuranceProse";

const URL = "https://virginiabariatric.com/es/cobertura-de-seguro";
const REVIEWED_ISO = "2026-10-26";

export const Route = createFileRoute("/es/cobertura-de-seguro")({
  head: () => {
    const base = esHead("insurance");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify(esWebPageSchema("insurance")),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqSchema(ES_FAQS, URL, "es")),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": procedureImageObjects(
              ES_PAGE_MEDIA["/es/cobertura-de-seguro"],
              "es",
              "https://virginiabariatric.com/es/cobertura-de-seguro",
            ),
          }),
        },
      ] : [],
    };
  },
  component: EsInsurance,
});

function EsInsurance() {
  return (
    <EsPageShell
      page="insurance"
      title="Cobertura de medicamentos para bajar de peso en Virginia"
      intro="La cobertura para la cirugía bariátrica y para los medicamentos de pérdida de peso funciona de manera distinta y cambia según el plan. Aquí explicamos los requisitos más frecuentes y cómo prepararse para que su solicitud sea aprobada."
    >
      <p className="text-center text-sm text-muted-foreground">
        Revisado por{" "}
        <Link to="/es/sobre-el-doctor" className="text-brand-deep underline hover:no-underline">
          Matthew Fitzer, MD, FACS
        </Link>{" "}
        &middot; Última revisión:{" "}
        <time dateTime={REVIEWED_ISO}>26 de octubre de 2026</time>
      </p>
      <EsSection id="cirugia" heading="Cobertura para la cirugía bariátrica">
        <p>
          La mayoría de los planes comerciales en Virginia cubre la manga gástrica y el bypass
          gástrico cuando se cumplen criterios médicos. Los requisitos habituales son:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>IMC de 40 o más, o IMC de 35 o más con una enfermedad relacionada con el peso.</li>
          <li>
            Documentación de intentos previos de bajar de peso, con frecuencia un programa
            supervisado de tres a seis meses.
          </li>
          <li>Evaluación psicológica y evaluación nutricional.</li>
          <li>
            Análisis de laboratorio y estudios previos según su historia clínica (por ejemplo,
            estudio del sueño o endoscopia).
          </li>
        </ul>
        <p>
          Nuestro personal envía la autorización previa por usted y da seguimiento a la
          aseguradora hasta obtener una respuesta.
        </p>
        <p>
          <Link
            to="/es/seguro-para-cirugia-bariatrica"
            className="font-medium text-brand-logo hover:underline"
          >
            Vea la guía completa, plan por plan, del seguro para cirugía bariátrica
          </Link>
          .
        </p>

      </EsSection>

      <EsSection id="medicamentos" heading="Cobertura para medicamentos GLP-1">
        <CheckpointPhoto lang="es" />
        <p>
          Los medicamentos como Wegovy y Zepbound se cubren a través del beneficio de farmacia, no
          del beneficio médico, y muchos planes los excluyen por completo. Cuando sí hay
          cobertura, casi siempre se exige autorización previa con documentación de IMC,
          diagnóstico y, en algunos planes, un periodo de participación en un programa
          supervisado.
        </p>
        <p>
          Medicare no cubre medicamentos recetados únicamente para la pérdida de peso, aunque sí
          puede cubrirlos con otra indicación aprobada. Medicaid de Virginia y el plan estatal
          tienen reglas propias que cambian con frecuencia.
        </p>
      </EsSection>

      <EsSection id="documentos" heading="Documentos que conviene reunir">
        <ul className="list-disc space-y-2 pl-5">
          <li>Su tarjeta del seguro, por ambos lados.</li>
          <li>Registro de pesos de los últimos dos años, si lo tiene.</li>
          <li>Lista de medicamentos actuales y diagnósticos relacionados con el peso.</li>
          <li>Constancias de programas de dieta o nutrición en los que haya participado.</li>
        </ul>
      </EsSection>

      <EsSection id="negacion" heading="Si le niegan la cobertura">
        <p>
          Una negación no es el final del proceso. Muchas se revierten en la apelación cuando se
          agrega documentación clínica que faltaba. Solicite siempre la carta de negación por
          escrito, revise el motivo exacto y respete el plazo de apelación indicado. Nuestro
          consultorio le ayuda a preparar la apelación y, si es necesario, la carta de necesidad
          médica del Dr. Fitzer.
        </p>
        <p>
          Si su plan excluye por completo estos servicios, existen{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            className="font-medium text-brand-logo hover:underline"
          >
            precios de pago propio
          </Link>{" "}
          y financiamiento; con gusto le damos las cifras actuales por teléfono.
        </p>

      </EsSection>

      <EsSection id="planes" heading="Cobertura plan por plan en Virginia" maxWidth="7xl">
        <p>
          Esta es la misma tabla de nuestra página en inglés, traducida al español. Muestra qué
          exige cada plan de Virginia antes de aprobar Zepbound, Wegovy o Saxenda. Las reglas
          cambian con frecuencia; confirme siempre con su plan la versión vigente.
        </p>
        <EsCoverageTable />
        <p className="text-sm text-muted-foreground">
          IMC significa índice de masa corporal. «Terapia escalonada» significa que el plan exige
          probar antes otro medicamento.
        </p>
        <SectionCta to="/es/contacto" label="cta_cobertura_after_planes_es" search={{ topic: "verificacion" }}>
          Solicite una verificación gratuita de beneficios
        </SectionCta>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes sobre la cobertura">
        <p>
          Las mismas preguntas que responde nuestra página en inglés, aquí en español.
        </p>
        <FaqList faqs={ES_FAQS} />
      </EsSection>

      <EsSection id="detalle" heading="Información detallada por tipo de plan">
        <p>
          Cada tipo de plan tiene reglas distintas. Estas guías en español explican los requisitos,
          la documentación y los tiempos de aprobación de cada uno.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Link
            to="/es/seguro/plan-comercial-y-marketplace"
            className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-soft/40"
          >
            <span className="block font-semibold text-brand-deep">
              Plan comercial y del Marketplace
            </span>
            <span className="block text-sm text-muted-foreground">
              Anthem, Aetna, Cigna, UnitedHealthcare y planes del Marketplace.
            </span>
          </Link>
          <Link
            to="/es/seguro/medicaid-y-cardinal-care"
            className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-soft/40"
          >
            <span className="block font-semibold text-brand-deep">
              Medicaid de Virginia y COVA
            </span>
            <span className="block text-sm text-muted-foreground">
              Cardinal Care y el plan estatal: qué cubren y cómo empezar.
            </span>
          </Link>
          <Link
            to="/es/seguro/medicare-tricare-y-fehb"
            className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-soft/40"
          >
            <span className="block font-semibold text-brand-deep">
              Medicare, TRICARE y FEHB
            </span>
            <span className="block text-sm text-muted-foreground">
              Programas federales y militares: criterios y límites.
            </span>
          </Link>
          <Link
            to="/es/seguro/pago-propio-y-negaciones"
            className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-soft/40"
          >
            <span className="block font-semibold text-brand-deep">
              Pago propio y apelaciones
            </span>
            <span className="block text-sm text-muted-foreground">
              Precios sin seguro, financiamiento y cómo apelar una negación.
            </span>
          </Link>
          <Link
            to="/es/seguro/carolina-del-norte"
            className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-soft/40 sm:col-span-2"
          >
            <span className="block font-semibold text-brand-deep">
              Carolina del Norte
            </span>
            <span className="block text-sm text-muted-foreground">
              State Health Plan, Medicaid de NC y planes comerciales.
            </span>
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">

          ¿Prefiere que le expliquemos su cobertura en español?{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            Escríbanos
          </Link>{" "}
          y revisamos su plan sin costo.
        </p>
      </EsSection>
    </EsPageShell>
  );
}
